import { useEffect } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { useDataFetchingClient } from 'data-fetching-client';
import Raven from 'raven-js';
import { useDispatch, useStore } from 'react-redux';
import useHandlePaymentFailure from 'payments-checkout-lib/hooks/useHandlePaymentFailure';
import { GET_CHECKOUT_SESSION, GET_PAYMENT_RESULT } from '../data/queries';
import { PENDING, SUBMITTED } from '../constants/Statuses';
import { isBnpl } from 'payments-checkout-lib/utils/paymentMethods';
import BuyerOverridesActionTypes from '../state/BuyerOverrides/actionTypes';
import { setTaxCalculationId } from '../state/calculateTaxSlice';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { runWhenTrackerInitialized, trackSuccessfulPaymentSubmission } from '../utils/tracking';
import { getBuyerOverridesLineItems } from '../state/BuyerOverrides/selectors';
import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../utils/sessionStorage';
export const BNPL_PAYMENT_METHOD_STORAGE_KEY = 'paymentsUiBnplPaymentMethod';
export const BNPL_BUYER_OVERRIDES_STORAGE_KEY = 'paymentsUiBnplBuyerOverrides';
export const BNPL_BUYER_EMAIL_STORAGE_KEY = 'paymentsUiBnplBuyerEmail';
export const setBnplInSessionStorage = paymentMethod => {
  setSessionStorageItem(BNPL_PAYMENT_METHOD_STORAGE_KEY, paymentMethod);
};
export const setBnplEmailInSessionStorage = email => {
  setSessionStorageItem(BNPL_BUYER_EMAIL_STORAGE_KEY, email);
};
export const storeBuyerOverridesForBnpl = buyerOverrides => {
  setSessionStorageItem(BNPL_BUYER_OVERRIDES_STORAGE_KEY, JSON.stringify(buyerOverrides));
};
const useHandleBnplRedirect = () => {
  const {
    search
  } = useLocation();
  const client = useDataFetchingClient();
  const dispatch = useDispatch();
  const store = useStore();
  const [handleFailedPayment] = useHandlePaymentFailure(GET_PAYMENT_RESULT);
  const {
    formState,
    dispatch: formDispatch
  } = usePaymentFormContext();
  useEffect(() => {
    var _checkoutSession$pref, _storedOverrides, _checkoutSession$subt, _checkoutSession$subt2;
    const storedPaymentMethod = getSessionStorageItem(BNPL_PAYMENT_METHOD_STORAGE_KEY);
    if (!storedPaymentMethod || !isBnpl(storedPaymentMethod)) {
      return;
    }
    const params = new URLSearchParams(search);
    const clientSecret = params.get('payment_intent_client_secret');
    const redirectStatus = params.get('redirect_status');
    if (!clientSecret || !redirectStatus) {
      return;
    }
    const getCheckoutSessionData = client.readQuery({
      query: GET_CHECKOUT_SESSION
    });
    const checkoutSession = getCheckoutSessionData === null || getCheckoutSessionData === void 0 ? void 0 : getCheckoutSessionData.checkoutSession;
    if (!checkoutSession) {
      return;
    }
    removeSessionStorageItem(BNPL_PAYMENT_METHOD_STORAGE_KEY);
    const storedOverridesRaw = getSessionStorageItem(BNPL_BUYER_OVERRIDES_STORAGE_KEY);
    removeSessionStorageItem(BNPL_BUYER_OVERRIDES_STORAGE_KEY);
    const storedEmail = getSessionStorageItem(BNPL_BUYER_EMAIL_STORAGE_KEY) || undefined;
    removeSessionStorageItem(BNPL_BUYER_EMAIL_STORAGE_KEY);
    if (storedEmail) {
      formDispatch({
        type: 'setField',
        data: {
          propertyPath: 'email',
          value: storedEmail
        }
      });
    }

    // Restore buyer overrides for all redirect outcomes. On processing/failure
    // the checkout remains active and needs prices/selections intact to work
    // correctly. On success the subtotals refetch uses the restored state.
    let storedOverrides = null;
    if (storedOverridesRaw) {
      try {
        const parsedOverrides = JSON.parse(storedOverridesRaw);
        storedOverrides = parsedOverrides;
        dispatch({
          type: BuyerOverridesActionTypes.RESTORE_STATE,
          data: {
            quantities: parsedOverrides.quantities,
            prices: parsedOverrides.prices,
            selections: parsedOverrides.selections
          }
        });
        if (parsedOverrides.taxCalculationId != null) {
          dispatch(setTaxCalculationId(parsedOverrides.taxCalculationId));
        }
      } catch (_unused) {
        // ignore malformed storage
      }
    }
    const email = storedEmail || checkoutSession.customerEmail || ((_checkoutSession$pref = checkoutSession.prefillData) === null || _checkoutSession$pref === void 0 ? void 0 : _checkoutSession$pref.email) || '';

    // If a discount code was applied before the redirect, patch it back into
    // subtotalsPublicResponse so useFetchDynamicSubtotals sends it to the
    // subtotals endpoint on the next render. The GET /checkout-sessions response
    // does not persist the discount code inside subtotalsPublicResponse.
    const subtotalsPublicResponse = (_storedOverrides = storedOverrides) !== null && _storedOverrides !== void 0 && _storedOverrides.discountCode ? Object.assign({}, checkoutSession.subtotalsPublicResponse, {
      discountCodeResponse: Object.assign({}, (_checkoutSession$subt = (_checkoutSession$subt2 = checkoutSession.subtotalsPublicResponse) === null || _checkoutSession$subt2 === void 0 ? void 0 : _checkoutSession$subt2.discountCodeResponse) !== null && _checkoutSession$subt !== void 0 ? _checkoutSession$subt : {}, {
        discountCode: storedOverrides.discountCode,
        isValid: true
      })
    }) : checkoutSession.subtotalsPublicResponse;
    switch (redirectStatus) {
      case 'succeeded':
        {
          const trackedFormState = Object.assign({}, formState, {
            paymentMethod: storedPaymentMethod
          });
          const lineItems = getBuyerOverridesLineItems(store.getState());
          runWhenTrackerInitialized(() => trackSuccessfulPaymentSubmission(trackedFormState, checkoutSession, lineItems));
          client.writeQuery({
            query: GET_CHECKOUT_SESSION,
            data: {
              checkoutSession: Object.assign({}, checkoutSession, {
                subtotalsPublicResponse,
                status: SUBMITTED,
                paymentDetails: {
                  paymentMethod: {
                    type: storedPaymentMethod
                  },
                  customer: {
                    email
                  }
                }
              })
            }
          });
          break;
        }
      case 'processing':
        client.writeQuery({
          query: GET_CHECKOUT_SESSION,
          data: {
            checkoutSession: Object.assign({}, checkoutSession, {
              subtotalsPublicResponse,
              status: PENDING,
              paymentDetails: {
                paymentMethod: {
                  type: storedPaymentMethod
                },
                customer: {
                  email
                }
              }
            })
          }
        });
        break;
      case 'failed':
        {
          var _storedOverrides2;
          handleFailedPayment({
            variables: {
              id: checkoutSession.id,
              clientSecret
            }
          });
          if ((_storedOverrides2 = storedOverrides) !== null && _storedOverrides2 !== void 0 && _storedOverrides2.discountCode) {
            client.writeQuery({
              query: GET_CHECKOUT_SESSION,
              data: {
                checkoutSession: Object.assign({}, checkoutSession, {
                  subtotalsPublicResponse
                })
              }
            });
          }
          break;
        }
      default:
        Raven.captureMessage('Buy now pay later redirect: unexpected redirect_status', {
          extra: {
            redirectStatus,
            storedPaymentMethod
          }
        });
        break;
    }
  }, [search, client, dispatch, store, handleFailedPayment, formDispatch, formState]);
};
export default useHandleBnplRedirect;